<template>
  <div>
    <Breadcrumbs title="Gestión de Usuarios" main="Consulta" />
    <div class="container-fluid">
      <div class="row">
        <usersTable
          :users="users"
          :loading="loading"
          @edit="handleEdit"
          @delete="handleDelete"
          @resetPassword="handleResetPassword"
        />
      </div>
    </div>

    <!-- Modal de edición -->
    <editUserModal
      :show="showEditModal"
      :user="selectedUser"
      @close="closeEditModal"
      @save="handleSaveUser"
    />

    <!-- Alertas -->
    <div
      v-if="alertMessage"
      :class="[
        'alert',
        alertType === 'success' ? 'alert-success' : 'alert-danger',
        'alert-dismissible',
        'fade',
        'show',
      ]"
      role="alert"
      style="position: fixed; top: 20px; right: 20px; z-index: 9999; min-width: 300px"
    >
      {{ alertMessage }}
      <button
        type="button"
        class="btn-close"
        @click="alertMessage = ''"
        aria-label="Close"
      ></button>
    </div>
  </div>
</template>

<script>
import Breadcrumbs from '@/layout/breadCrumbs.vue'
import usersTable from '@/components/table/usersTable.vue'
import editUserModal from '@/components/users/editUserModal.vue'
import { getAllUsers, updateUser, deleteUser, recordPasswordResetSent } from '@/services/usersService'
import { sendPasswordReset } from '@/services/authService'
import { mapGetters } from 'vuex'

export default {
  name: 'ConsultaUsuarios',
  components: {
    Breadcrumbs,
    usersTable,
    editUserModal,
  },
  computed: {
    ...mapGetters('auth', ['userRole', 'currentUser']),
  },
  data() {
    return {
      users: [],
      loading: false,
      error: null,
      showEditModal: false,
      selectedUser: null,
      alertMessage: '',
      alertType: 'success',
    }
  },
  async created() {
    await this.loadUsers()
  },
  methods: {
    async loadUsers() {
      this.loading = true
      this.error = null

      try {
        const result = await getAllUsers()

        if (result.success) {
          this.users = result.data || []
        } else {
          this.error = result.error || 'Error al cargar usuarios'
          this.users = []
        }
      } catch (error) {
        console.error('Error loading users:', error)
        this.error = 'Error inesperado al cargar usuarios'
        this.users = []
      } finally {
        this.loading = false
      }
    },
    handleEdit(user) {
      this.selectedUser = user
      this.showEditModal = true
    },
    closeEditModal() {
      this.showEditModal = false
      this.selectedUser = null
    },
    async handleSaveUser(userData) {
      try {
        const currentUserRole = this.userRole || (this.currentUser && this.currentUser.rol)
        const actor = this.currentUser

        if (!actor?.uid) {
          this.showAlert('No se pudo identificar al usuario. Vuelva a iniciar sesión.', 'error')
          return
        }

        const result = await updateUser(
          userData.uid,
          {
            name: userData.name,
            defaultStore: userData.defaultStore,
            rol: userData.rol,
            status: userData.status,
          },
          currentUserRole,
          actor
        )

        if (result.success) {
          this.showAlert('Usuario actualizado correctamente', 'success')
          await this.loadUsers() // Recargar lista
          this.closeEditModal()
        } else {
          this.showAlert(result.error || 'Error al actualizar usuario', 'error')
        }
      } catch (error) {
        console.error('Error saving user:', error)
        this.showAlert('Error inesperado al actualizar usuario', 'error')
      }
    },
    async handleDelete(user) {
      const result = await this.$swal({
        title: '¿Está seguro de eliminar al usuario?',
        text: `${user.name || user.email}`,
        html: `Esta acción <strong>no se puede deshacer</strong>. El usuario será eliminado permanentemente del sistema.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        reverseButtons: true,
        focusConfirm: false,
        focusCancel: true,
      })

      if (!result.isConfirmed) {
        return
      }

      try {
        const deleteResult = await deleteUser(user.uid, this.currentUser)

        if (deleteResult.success) {
          await this.$swal({
            title: '¡Eliminado!',
            text: 'El usuario ha sido eliminado correctamente',
            icon: 'success',
            confirmButtonColor: '#28a745',
            confirmButtonText: 'Aceptar',
          })
          await this.loadUsers() // Recargar lista
        } else {
          await this.$swal({
            title: 'Error',
            text: deleteResult.error || 'Error al eliminar usuario',
            icon: 'error',
            confirmButtonColor: '#dc3545',
            confirmButtonText: 'Aceptar',
          })
        }
      } catch (error) {
        console.error('Error deleting user:', error)
        await this.$swal({
          title: 'Error',
          text: 'Error inesperado al eliminar usuario',
          icon: 'error',
          confirmButtonColor: '#dc3545',
          confirmButtonText: 'Aceptar',
        })
      }
    },
    showAlert(message, type = 'success') {
      this.alertMessage = message
      this.alertType = type
      setTimeout(() => {
        this.alertMessage = ''
      }, 5000)
    },
    async handleResetPassword(user) {
      const result = await this.$swal({
        title: '¿Reenviar correo de restablecimiento?',
        text: `Se enviará un correo a ${user.email}`,
        html: `Se enviará un correo de restablecimiento de contraseña a <strong>${user.email}</strong>.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, reenviar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#17a2b8',
        cancelButtonColor: '#6c757d',
        reverseButtons: true,
        focusConfirm: false,
        focusCancel: true,
      })

      if (!result.isConfirmed) {
        return
      }

      try {
        // Mostrar loading mientras se envía
        this.$swal({
          title: 'Enviando correo...',
          text: 'Por favor espera',
          icon: 'info',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          willOpen: () => {
            this.$swal.showLoading()
          },
        })

        const resetResult = await sendPasswordReset(user.email)

        this.$swal.close()

        if (resetResult.success) {
          const auditResult = await recordPasswordResetSent(user.uid, this.currentUser)
          if (!auditResult.success) {
            console.error('Error recording password reset audit:', auditResult.error)
          }

          await this.$swal({
            title: '¡Correo enviado!',
            text:
              resetResult.message || 'Se ha enviado un correo de restablecimiento de contraseña',
            icon: 'success',
            confirmButtonColor: '#28a745',
            confirmButtonText: 'Aceptar',
          })
          await this.loadUsers()
        } else {
          await this.$swal({
            title: 'Error',
            text: resetResult.error || 'Error al enviar el correo de restablecimiento',
            icon: 'error',
            confirmButtonColor: '#dc3545',
            confirmButtonText: 'Aceptar',
          })
        }
      } catch (error) {
        console.error('Error sending password reset:', error)
        this.$swal.close()
        await this.$swal({
          title: 'Error',
          text: 'Error inesperado al enviar el correo de restablecimiento',
          icon: 'error',
          confirmButtonColor: '#dc3545',
          confirmButtonText: 'Aceptar',
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.alert {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}
</style>
